
import React from 'react';
import { Product } from '@/lib/supabase/products';
import ProductsTable from './ProductsTable';
import SelectionSummary from './SelectionSummary';
import { Button } from '@/components/ui/button';
import { Trash2, Download, PlusCircle } from 'lucide-react';
import { exportProductsToCSV } from '../utils/exportUtils';
import { Skeleton } from '@/components/ui/skeleton';

interface ProductsListViewProps {
  products: Product[];
  loading: boolean;
  selectedProducts: string[];
  toggleProductSelection: (id: string) => void;
  handleEditProduct: (id: string) => void;
  handleDeleteProduct: (id: string) => void;
  handleBulkDeleteProducts: (ids: string[]) => void;
  handleAddProduct: () => void;
  formatDate: (dateString: string) => string;
  toggleSelectAll: () => void;
  areAllSelected: boolean;
}

const ProductsListView = ({
  products,
  loading,
  selectedProducts,
  toggleProductSelection,
  handleEditProduct,
  handleDeleteProduct,
  handleBulkDeleteProducts,
  handleAddProduct,
  formatDate,
  toggleSelectAll,
  areAllSelected
}: ProductsListViewProps) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }
  
  return (
    <>
      {selectedProducts.length > 0 && (
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b dark:border-gray-700 mb-4 py-3 px-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="font-medium dark:text-gray-200">
              {selectedProducts.length} {selectedProducts.length === 1 ? 'sélectionné' : 'sélectionnés'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportProductsToCSV(products, selectedProducts)}
              className="gap-2 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              <Download className="h-4 w-4" />
              Exporter CSV
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => handleBulkDeleteProducts(selectedProducts)}
              className="gap-2 dark:bg-red-700 dark:hover:bg-red-800"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={toggleSelectAll}
              className="gap-2 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
      
      <div className="shadow-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
        <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Produits ({products.length})</h3>
          <div className="flex gap-2">
            <Button 
              onClick={handleAddProduct}
              className="add-button"
              size="sm"
              title="Ajouter un produit"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Ajouter un produit
            </Button>
          </div>
        </div>
        
        <ProductsTable 
          products={products}
          selectedProducts={selectedProducts}
          toggleProductSelection={toggleProductSelection}
          handleEditProduct={handleEditProduct}
          handleDeleteProduct={handleDeleteProduct}
          formatDate={formatDate}
          toggleSelectAll={toggleSelectAll}
          areAllSelected={areAllSelected}
        />
      </div>
      
      <SelectionSummary 
        selectedCount={selectedProducts.length} 
        totalCount={products.length} 
      />
    </>
  );
};

export default ProductsListView;
